import React from 'react';
import { useTranslation } from 'react-i18next';
import SelectField from '../../../components/ui/SelectField';
import OrgDetailSection from './OrgDetailSection';
import { CustomerClass } from '../../../api/organization/organizationApiTypes';
import {
    NO_CLASS_VALUE,
    buildCustomerClassOptions,
    hasCustomerClassChoice,
} from '../organizationSectionVisibility';

interface OrgClassSectionProps {
    /** The selected org-default customer-class id, or null for "none". */
    value: string | null;
    onChange: (customerClassId: string | null) => void;
    classes: CustomerClass[];
    isLoading?: boolean;
    /** Render read-only (view mode): select disabled, no editing. */
    disabled?: boolean;
}

/**
 * Org-level default customer-class select (plan §4.4).
 *
 * The option list — and why "none" is shown under the default class's name
 * rather than as a second entry meaning the same tariff — lives in
 * `buildCustomerClassOptions`, which the create and detail pages share.
 *
 * The field stays visible even when the tenant has nothing to choose between,
 * so the axis is discoverable: an organization *can* be given its own tariff.
 * The hint says where that comes from rather than pretending it is a choice
 * today (PO, 2026-08-19).
 */
const OrgClassSection: React.FC<OrgClassSectionProps> = ({
    value,
    onChange,
    classes,
    isLoading = false,
    disabled = false,
}) => {
    const { t } = useTranslation();

    const { values, labels } = React.useMemo(
        () => buildCustomerClassOptions(classes, t('organization.fields.customer_class_none')),
        [classes, t],
    );
    const hasChoice = React.useMemo(() => hasCustomerClassChoice(classes), [classes]);

    return (
        <OrgDetailSection
            title={t('organization.sections.customer_class')}
            subtitle={t('organization.sections.customer_class_subtitle')}
        >
            <div className={'flex flex-col gap-1'}>
                <SelectField
                    labelAbove={true}
                    label={t('organization.fields.customer_class')}
                    help={t('organization.help.customer_class')}
                    value={value ?? NO_CLASS_VALUE}
                    onChange={(val) => onChange(val === NO_CLASS_VALUE ? null : val)}
                    options={values}
                    optionLabels={labels}
                    disabled={isLoading || disabled}
                    noOptions={t('organization.fields.customer_class_none')}
                />
                {!hasChoice && !isLoading && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('organization.fields.customer_class_single_hint')}
                    </p>
                )}
            </div>
        </OrgDetailSection>
    );
};

export default OrgClassSection;
